import toast from 'react-hot-toast';
import type { useMutation, useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { z } from 'zod';

import { APIEndPoints, Request } from '../apis';
import { defaultPagination } from '../common';
import type { TLoan } from '../common/types';
import { LoanDetailSchema } from '../common/types';
import type {
  TMerchant,
  TLoanFilterOptions,
  TPagination
} from '../components/types';

type MambuLoanStoreState = {
  isLoading: boolean;
  searchPagination: TPagination;
  searchResults: Array<TLoan>;
  clearState: () => void;
  setSearchPagination: (settings: TPagination) => void;
  setSearchResults: (loans: Array<TLoan>) => void;
  setLoading: (loading: boolean) => void;
  setSuccess?: React.Dispatch<React.SetStateAction<boolean>>;
  setError?: React.Dispatch<React.SetStateAction<boolean>>;
  setFormLoading?: React.Dispatch<React.SetStateAction<boolean>>;
};

const STARTING_STATE = {
  isLoading: true,
  searchPagination: { ...defaultPagination } as TPagination,
  searchResults: [] as Array<TLoan>
} as const;

export const useMambuLoanStore = create<MambuLoanStoreState>((set) => ({
  ...STARTING_STATE,
  setLoading: (loading: boolean) => {
    set({ isLoading: loading });
  },
  setSearchPagination: (settings = { ...defaultPagination }) => {
    set({ searchPagination: settings });
  },
  setSearchResults: (loans = []) => {
    set({ searchResults: loans });
  },
  clearState: () => {
    set({ ...STARTING_STATE });
  }
}));

export const clearMambuLoanStore = () => {
  useMambuLoanStore.setState({ ...STARTING_STATE });
};

export const requestFunds = ({
  mambuLoanId,
  merchantId,
  fundingDate,
  mutate,
  loanApplicationSalesAgentUserId,
  loanApplicationSalesAgentEmail,
  setSuccess,
  setError,
  setFormLoading
}: {
  mambuLoanId: string;
  merchantId: string;
  fundingDate: string;
  mutate: typeof useMutation;
  loanApplicationSalesAgentUserId?: string;
  loanApplicationSalesAgentEmail?: string;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  setFormLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) =>
  mutate({
    mutationFn: () => {
      // TODO log
      // console.log(
      //   `requestFunds mutationFn mambuLoanId: ${mambuLoanId}, merchantId: ${merchantId}, fundingDate: ${fundingDate}`
      // );
      setFormLoading(true);
      return Request.post(
        `${APIEndPoints.mambuLoan}/${mambuLoanId}/requestFunds?access_token`,
        z.any(),
        { fundingDate },
        {
          merchantId,
          loanApplicationSalesAgentUserId:
            loanApplicationSalesAgentUserId ?? '',
          loanApplicationSalesAgentEmail: loanApplicationSalesAgentEmail ?? ''
        }
      );
    },
    onSettled: () => {
      setFormLoading(false);
    },
    onSuccess: async (data: TMerchant) => {
      setSuccess(true);
    },
    onError: (error) => {
      setError(true);
      toast.error('Error encountered requesting funds');
    }
  });

export const activatePlan = ({
  mambuLoanId,
  merchantId,
  activationDate,

  mutate,
  loanApplicationSalesAgentUserId,
  loanApplicationSalesAgentEmail,
  setFormLoading,
  setSuccess,
  setError
}: {
  mambuLoanId: string;
  merchantId: string;
  activationDate: string;
  mutate: typeof useMutation;
  loanApplicationSalesAgentUserId?: string;
  loanApplicationSalesAgentEmail?: string;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  setFormLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) =>
  mutate({
    mutationFn: () => {
      // TODO log
      // console.log(
      //   `activatePlan mutationFn mambuLoanId: ${mambuLoanId}, merchantId: ${merchantId}, activationDate: ${activationDate}`
      // );
      setFormLoading(true);
      return Request.post(
        `${APIEndPoints.mambuLoan}/${mambuLoanId}/activatePlan?access_token`,
        z.any(),
        { activationDate },
        {
          merchantId,
          loanApplicationSalesAgentUserId:
            loanApplicationSalesAgentUserId ?? '',
          loanApplicationSalesAgentEmail: loanApplicationSalesAgentEmail ?? ''
        }
      );
    },
    onSettled: () => {
      setFormLoading(false);
    },
    onSuccess: async (data: TMerchant) => {
      setSuccess(true);
    },
    onError: (error) => {
      setError(true);
      toast.error('Error encountered activating plan');
    }
  });

export const updateCourseEndDate = ({
  mambuLoanId,
  merchantId,
  courseEndDate,

  mutate,
  loanApplicationSalesAgentUserId,
  loanApplicationSalesAgentEmail,
  setFormLoading,
  setSuccess,
  setError
}: {
  mambuLoanId: string;
  merchantId: string;
  courseEndDate: string;
  mutate: typeof useMutation;
  loanApplicationSalesAgentUserId?: string;
  loanApplicationSalesAgentEmail?: string;
  setSuccess: React.Dispatch<React.SetStateAction<boolean>>;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  setFormLoading: React.Dispatch<React.SetStateAction<boolean>>;
}) =>
  mutate({
    mutationFn: () => {
      setFormLoading(true);
      return Request.patch(
        `${APIEndPoints.mambuLoan}/${mambuLoanId}/end-date?access_token`,
        z.any(),
        { courseEndDate },
        {
          merchantId,
          loanApplicationSalesAgentUserId:
            loanApplicationSalesAgentUserId ?? '',
          loanApplicationSalesAgentEmail: loanApplicationSalesAgentEmail ?? ''
        }
      );
    },
    onSettled: () => {
      setFormLoading(false);
    },
    onSuccess: async (data: TMerchant) => {
      setSuccess(true);
    },
    onError: (error) => {
      setError(true);
      toast.error('Error encountered updating course end data');
    }
  });

export const getMambuLoanDetails = async (mambuAccountId: string) => {
  const response = await Request.safeGet({
    path: `${APIEndPoints.mambuLoan}/${mambuAccountId}`,
    schema: LoanDetailSchema
  });
  if (response.type === 'right') {
    return response.value;
  }
  throw new Error(`Failed to get mambu loan details for ${mambuAccountId}`, {
    cause: response.value
  });
};

export const queryMambuLoanDetails = ({
  mambuAccountId,
  setLoading,
  query
}: {
  mambuAccountId: string;
  setLoading: MambuLoanStoreState['setLoading'];
  query: typeof useQuery;
}) =>
  query(
    ['mambuLoan', mambuAccountId],
    async () => {
      setLoading(true);
      return getMambuLoanDetails(mambuAccountId);
    },
    {
      onSettled: () => {
        setLoading(false);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to retrieve loan details. Please refresh and try again.'
        );
      },
      // staleTime: 24 * HOUR
      // cacheTime: 0,
      refetchInterval: false,
      // refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 2000
    }
  );

export const searchMambuLoans = ({
  merchantId,
  filter = {},
  pagination = { ...defaultPagination } as TPagination,
  productType = '',
  setLoading,
  setSearchPagination,
  setSearchResults,
  query
}: {
  merchantId: string;
  filter: TLoanFilterOptions;
  pagination: TPagination;
  productType?: string;
  setLoading: MambuLoanStoreState['setLoading'];
  setSearchPagination: MambuLoanStoreState['setSearchPagination'];
  setSearchResults: MambuLoanStoreState['setSearchResults'];
  query: typeof useQuery;
}) =>
  query(
    [
      'mambuLoanSearch',
      productType,
      JSON.stringify(filter),
      pagination.currentPage
    ],
    async () => {
      const { currentPage, pageSize: paginationPageSize } = pagination;
      const { includeMigrated, ...searchFilter } = filter;
      // handle product types
      let productTypes: string[] = [];
      if (includeMigrated) {
        productTypes =
          productType === 'PAS' ? ['PAS', 'PAD', 'ZAS'] : ['SNP', 'ZNP', 'ZNT'];
      } else {
        productTypes = productType === 'PAS' ? ['PAS', 'PAD'] : ['SNP'];
      }
      setLoading(true);
      const response = await Request.safePost(
        `${APIEndPoints.loansSearch}/?access_token`,
        z.any(),
        {
          merchantId: merchantId,
          fullDetail: true,
          productTypes,
          paginationPageSize,
          paginationPageOffset: Math.max(currentPage - 1, 0),
          ...Object.fromEntries(
            Object.entries(searchFilter).filter(([k, v]) => !!v)
          )
        }
      );
      if (response.type === 'right') {
        return response.value;
      }
      throw new Error('Failed to search mambu loans', {
        cause: response.value
      });
    },
    {
      onSettled: () => {
        setLoading(false);
      },
      onSuccess: ({
        loanCount: totalCount = 0,
        loans = []
      }: {
        loanCount: number;
        loans: Array<TLoan>;
      }) => {
        setSearchPagination({
          ...pagination,
          totalCount
        });
        setSearchResults(loans);
      },
      onError: (error) => {
        toast.error(
          'Something went wrong while trying to search mambu loans. Please refresh and try again.'
        );
        setSearchPagination({
          ...pagination,
          currentPage: 1,
          totalCount: 0
        });
        setSearchResults([]);
      },
      // staleTime: 24 * HOUR
      // cacheTime: 0,
      refetchInterval: false,
      // refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: 1,
      retryDelay: 2000
    }
  );
